// src/pages/cadastros/Cooperadores/NovoCooperador.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useTheme } from "../../../contexts/ThemeContext";
import { useAuth } from "../../../contexts/AuthContext";
import { useEmpresa } from "../../../contexts/EmpresaContext";
import { useIsMobile } from "../../../hooks/useIsMobile";

import LayoutDesktop from "../../../layouts/LayoutDesktop";
import LayoutMobile from "../../../layouts/LayoutMobile";

import { pode, nivelUsuario } from "../../../utils/permissoes";

import { db } from "../../../config/firebase";
import {
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";

function labelNivel(u) {
  const n = nivelUsuario(u);
  if (n === 6) return "Gestor";
  if (n === 5) return "Administrador";
  if (n === 4) return "Técnico Sênior";
  if (n === 3) return "Técnico";
  return u?.nivel ?? "-";
}

const COMPETENCIAS_PADRAO = [
  "Elétrica",
  "Eletrônica",
  "Automação",
  "Mecânica",
  "Robótica",
  "Hidráulica",
  "Pneumática",
  "Encanamento",
  "Instrumentação",
  "Metrologia",
  "Refrigeração",
  "Solda",
  "Lubrificação/PCM",
  "Outros",
];

function NovoCooperador() {
  const { tema, alterarTema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const { modoGlobal, empresaAtual } = useEmpresa();
  const navigate = useNavigate();
  const isMobile = useIsMobile(900);

  const [sidebarAberta, setSidebarAberta] = useState(false);

  const podeCriar = pode(usuarioLogado, "cadastros", "cooperadores", "form");

  const [usuarios, setUsuarios] = useState([]);
  const [carregandoUsuarios, setCarregandoUsuarios] = useState(false);
  const [buscaUsuario, setBuscaUsuario] = useState("");

  const [usuarioSelecionadoId, setUsuarioSelecionadoId] = useState("");
  const usuarioSelecionado = useMemo(
    () => usuarios.find((u) => u.id === usuarioSelecionadoId) || null,
    [usuarios, usuarioSelecionadoId]
  );

  const [competencias, setCompetencias] = useState([
    { valor: "Elétrica", outros: "" },
  ]);
  const [horarioEntrada, setHorarioEntrada] = useState("");
  const [horarioSaida, setHorarioSaida] = useState("");
  const [ativo, setAtivo] = useState(true);

  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [carregando, setCarregando] = useState(false);

  const inputStyle = {
    width: "100%",
    padding: "9px 11px",
    marginTop: 4,
    borderRadius: 8,
    border: "1px solid #334155",
    boxSizing: "border-box",
  };

  useEffect(() => {
    async function carregarUsuarios() {
      if (!usuarioLogado?.grupoId) return;

      setCarregandoUsuarios(true);
      setErro("");

      try {
        const ref = collection(db, "usuarios");
        const q = query(ref, where("grupoId", "==", usuarioLogado.grupoId));
        const snap = await getDocs(q);

        const lista = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((u) => {
            const n = nivelUsuario(u);
            return n === 3 || n === 4 || n === 5 || n === 6;
          })
          .map((u) => ({
            ...u,
            __labelNome: u.nome || u.displayName || u.email || u.id,
            __labelNivel: labelNivel(u),
          }))
          .sort((a, b) => String(a.__labelNome).localeCompare(String(b.__labelNome)));

        setUsuarios(lista);
      } catch (e) {
        console.error("Erro ao carregar usuários:", e);
        setUsuarios([]);
        setErro("Erro ao carregar lista de usuários.");
      } finally {
        setCarregandoUsuarios(false);
      }
    }

    if (podeCriar) carregarUsuarios();
    else setErro("Sem permissão para cadastrar Cooperadores.");
  }, [usuarioLogado?.grupoId, podeCriar]);

  const usuariosFiltrados = useMemo(() => {
    const t = buscaUsuario.trim().toLowerCase();
    if (!t) return usuarios;
    return usuarios.filter((u) => {
      const base = `${u.__labelNome} ${u.__labelNivel} ${u.email || ""}`.toLowerCase();
      return base.includes(t);
    });
  }, [usuarios, buscaUsuario]);

  function addCompetencia() {
    setCompetencias((prev) => [...prev, { valor: "Elétrica", outros: "" }]);
  }

  function removeCompetencia(idx) {
    setCompetencias((prev) => prev.filter((_, i) => i !== idx));
  }

  function updateCompetencia(idx, patch) {
    setCompetencias((prev) => prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  }

  function buildCompetenciasFinal() {
    const out = [];
    for (const c of competencias) {
      const v = (c?.valor || "").trim();
      if (!v) continue;
      if (v === "Outros") {
        const o = (c?.outros || "").trim();
        if (o) out.push(o);
      } else {
        out.push(v);
      }
    }
    // remove duplicadas
    return Array.from(new Set(out));
  }

  async function handleSubmit(e) {
    e.preventDefault();

    setErro("");
    setSucesso("");

    if (!podeCriar) {
      setErro("Sem permissão para cadastrar cooperadores.");
      return;
    }

    if (!usuarioLogado?.grupoId) {
      setErro("Usuário sem grupoId. Faça login novamente.");
      return;
    }

    if (!usuarioSelecionadoId) {
      setErro("Selecione um usuário.");
      return;
    }

    const compFinal = buildCompetenciasFinal();
    if (!compFinal.length) {
      setErro("Adicione pelo menos 1 competência.");
      return;
    }

    const grupoId = usuarioLogado.grupoId;
    const grupoNome = usuarioLogado.grupoNome || null;

    const empresaId = !modoGlobal && empresaAtual?.id ? empresaAtual.id : null;
    const empresaNome = !modoGlobal && empresaAtual?.nome ? empresaAtual.nome : null;

    setCarregando(true);

    try {
      const ref = doc(collection(db, "cooperadores"));

      const nome = usuarioSelecionado?.__labelNome || usuarioSelecionadoId;
      const cargo = usuarioSelecionado?.__labelNivel || labelNivel(usuarioSelecionado);
      const nivelNum = nivelUsuario(usuarioSelecionado);

      await setDoc(ref, {
        usuarioId: usuarioSelecionadoId,
        usuarioNome: nome,
        usuarioEmail: usuarioSelecionado?.email || null,

        cargo,
        nivel: usuarioSelecionado?.nivel ?? null,
        nivelNum: Number.isFinite(nivelNum) ? nivelNum : null,

        competencias: compFinal,
        horarioEntrada: (horarioEntrada || "").trim() || null,
        horarioSaida: (horarioSaida || "").trim() || null,

        ativo: !!ativo,

        grupoId,
        grupoNome,
        empresaId,
        empresaNome,

        criadoPor: usuarioLogado?.uid || null,
        criadoEm: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setSucesso("Cooperador cadastrado com sucesso.");

      setUsuarioSelecionadoId("");
      setBuscaUsuario("");
      setCompetencias([{ valor: "Elétrica", outros: "" }]);
      setHorarioEntrada("");
      setHorarioSaida("");
      setAtivo(true);

      setTimeout(() => navigate("/cadastros/cooperadores"), 350);
    } catch (e2) {
      console.error("Erro ao cadastrar cooperador:", e2);
      setErro(e2?.message || "Erro ao cadastrar cooperador.");
    } finally {
      setCarregando(false);
    }
  }

  const Layout = isMobile ? LayoutMobile : LayoutDesktop;

  return (
    <Layout
      tema={tema}
      alterarTema={alterarTema}
      titulo="Cooperadores"
      subtitulo={isMobile ? "" : "Cadastro de Cooperadores da Manutenção"}
      sidebarAberta={sidebarAberta}
      onToggleSidebar={isMobile ? () => setSidebarAberta(!sidebarAberta) : undefined}
      abaBottom={isMobile ? "cadastros" : undefined}
    >
      <section className="pc-card" style={{ maxWidth: 980, margin: "0 auto" }}>
        <header style={{ marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>Novo Cooperador (Manutenção)</h3>
          <p style={{ margin: "6px 0 0", opacity: 0.8 }}>
            Vincule um usuário e defina competências e horários.
          </p>
        </header>

        {erro && (
          <div className="pc-alert pc-alert-erro" style={{ marginBottom: 12 }}>
            {erro}
          </div>
        )}
        {sucesso && (
          <div className="pc-alert pc-alert-ok" style={{ marginBottom: 12 }}>
            {sucesso}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Usuário */}
          <div style={{ marginBottom: 14 }}>
            <label>
              <span style={{ fontSize: 14 }}>Usuário</span>
              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8, marginTop: 4 }}>
                <input
                  type="text"
                  placeholder="Buscar usuário (nome, email, cargo...)"
                  value={buscaUsuario}
                  onChange={(e) => setBuscaUsuario(e.target.value)}
                  style={inputStyle}
                />

                <select
                  value={usuarioSelecionadoId}
                  onChange={(e) => setUsuarioSelecionadoId(e.target.value)}
                  disabled={carregandoUsuarios}
                  style={inputStyle}
                >
                  <option value="">{carregandoUsuarios ? "Carregando..." : "Selecione"}</option>
                  {usuariosFiltrados.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.__labelNome} — {u.__labelNivel}
                    </option>
                  ))}
                </select>

                <div style={{ fontSize: 12, opacity: 0.75 }}>
                  Permitidos: Gestor, Administrador, Técnico Sênior e Técnico.
                </div>
              </div>
            </label>
          </div>

          {/* Cargo (automático) */}
          <div style={{ marginBottom: 14 }}>
            <label>
              <span style={{ fontSize: 14 }}>Cargo (automático)</span>
              <input
                value={usuarioSelecionado ? usuarioSelecionado.__labelNivel : ""}
                readOnly
                placeholder="Selecione um usuário"
                style={{ ...inputStyle, opacity: usuarioSelecionado ? 1 : 0.7 }}
              />
            </label>
          </div>

          {/* Competências */}
          <div style={{ marginBottom: 14 }}>
            <span style={{ fontSize: 14, display: "block", marginBottom: 6 }}>Competências</span>

            <div style={{ display: "grid", gap: 10 }}>
              {competencias.map((c, idx) => (
                <div
                  key={`${idx}-${c.valor}`}
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "1.2fr 1.3fr auto",
                    gap: 8,
                    alignItems: "center",
                  }}
                >
                  <select
                    value={c.valor}
                    onChange={(e) => updateCompetencia(idx, { valor: e.target.value })}
                    style={inputStyle}
                  >
                    {COMPETENCIAS_PADRAO.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>

                  {c.valor === "Outros" ? (
                    <input
                      type="text"
                      value={c.outros}
                      onChange={(e) => updateCompetencia(idx, { outros: e.target.value })}
                      placeholder="Descreva (ex: CLP Siemens, Inversores, etc.)"
                      style={inputStyle}
                    />
                  ) : (
                    <div style={{ fontSize: 12, opacity: 0.7, paddingLeft: 6 }}>
                      {/* espaçador */}
                    </div>
                  )}

                  <div style={{ display: "flex", gap: 8, justifyContent: isMobile ? "flex-end" : "center" }}>
                    <button
                      type="button"
                      className="pc-btn-secundario"
                      onClick={() => removeCompetencia(idx)}
                      disabled={competencias.length <= 1}
                      title={competencias.length <= 1 ? "Mínimo 1 competência" : "Remover"}
                      style={{ padding: "8px 10px" }}
                    >
                      Remover
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 10 }}>
              <button type="button" className="pc-btn-secundario" onClick={addCompetencia}>
                + Adicionar competência
              </button>
            </div>
          </div>

          {/* Horários */}
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
            <label>
              <span style={{ fontSize: 14 }}>Horário Entrada</span>
              <input
                type="time"
                value={horarioEntrada}
                onChange={(e) => setHorarioEntrada(e.target.value)}
                style={inputStyle}
              />
            </label>

            <label>
              <span style={{ fontSize: 14 }}>Horário Saída</span>
              <input
                type="time"
                value={horarioSaida}
                onChange={(e) => setHorarioSaida(e.target.value)}
                style={inputStyle}
              />
            </label>
          </div>

          {/* Ativo */}
          <div style={{ marginTop: 14 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />
              <span style={{ fontSize: 14 }}>Ativo</span>
            </label>
          </div>

          {/* Ações */}
          <div
            style={{
              marginTop: 18,
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
            }}
          >
            <button
              type="button"
              className="pc-btn-secundario"
              onClick={() => navigate("/cadastros/cooperadores")}
            >
              Cancelar
            </button>
            <button type="submit" className="pc-btn-primario" disabled={carregando}>
              {carregando ? "Salvando..." : "Salvar cooperador"}
            </button>
          </div>
        </form>
      </section>
    </Layout>
  );
}

export default NovoCooperador;
